import { defineComponent as _defineComponent } from 'vue';
import { renderSlot as _renderSlot, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, withCtx as _withCtx, unref as _unref, withModifiers as _withModifiers, withKeys as _withKeys, createVNode as _createVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, vShow as _vShow, withDirectives as _withDirectives } from "vue";
const _hoisted_1 = { class: "data-table__top" };
const _hoisted_2 = { class: "data-table__filters" };
const _hoisted_3 = {
    key: 0,
    class: "data-table__title-slot"
};
const _hoisted_4 = { class: "data-table__title" };
const _hoisted_5 = ["title"];
const _hoisted_6 = { key: 2 };
const _hoisted_7 = { class: "data-table__actions" };
const _hoisted_8 = { class: "data-table__main" };
const _hoisted_9 = { class: "data-table__header" };
const _hoisted_10 = { class: "data-table__column data-table__column--list" };
const _hoisted_11 = {
    key: 0,
    class: "data-table__check"
};
const _hoisted_12 = ["checked"];
const _hoisted_13 = { class: "data-table__header-value" };
const _hoisted_14 = ["title"];
const _hoisted_15 = { class: "data-table__column-label" };
const _hoisted_16 = { class: "data-table__more" };
const _hoisted_17 = {
    key: 0,
    class: "data-table__too-many-results"
};
import { computed, onMounted, provide, ref, shallowRef, toRef, useSlots, watchEffect } from 'vue';
import userSettings from "../services/UserSettings";
import SearchFilters from "./SearchFilters.vue";
import Spinner from "./Spinner.vue";
import Help from "./Help.vue";
import BadgeComponent from "./BadgeComponent.vue";
import DataTableColumnSort from "./data-table/DataTableColumnSort.vue";
import DataTableColumnsSelection from "./data-table/DataTableColumnsSelection.vue";
import DataTableStatistics from "./data-table/DataTableStatistics.vue";
import DataTableBulkOperations from "./data-table/DataTableBulkOperations.vue";
import DataTableListRow from "./data-table/DataTableListRow.vue";
import DataTableRow from "./data-table/DataTableRow.vue";
import { numberFilter } from "../util/CommonUiFilters";
import trans from "../services/Translator";
import favouriteItemsService from "../services/FavouriteItemsService";
import { applyColumnUserSettings, areColumnsModified, colWidth, createDefaultColumns, cssName, hasStatusBadgeColumn, reorderColumns, resetColumns as resetColumnsToDefaults, resizeColumn as resizeColumnTo, selectedColumnNames as namesOfSelectedColumns, toColumnUserSettings, toggleColumnSelected, toggleColumnSort, userSettingsKey, } from "./data-table/columns";
import { createFilters, hasFavouritesFilter, prepareSearchRequest, resolveInitialSearchRequest, } from "./data-table/searchRequest";
import { countSelected, emptySelection, excludedIds, selectedIds, toggleAllSelections, toggleSelection, } from "./data-table/itemSelections";
import { DATA_TABLE_CONTEXT } from "./data-table/context";
import { useDataTableLayout } from "./data-table/useDataTableLayout";
import { useDataTableItems } from "./data-table/useDataTableItems";
import { useDataTableActiveItem } from "./data-table/useDataTableActiveItem";
import { useDataTableEvents } from "./data-table/useDataTableEvents";
export default /*@__PURE__*/ _defineComponent({
    __name: 'DataTable',
    props: {
        grid: {},
        source: {},
        name: {},
        title: {},
        description: {},
        helpCode: {},
        translationPrefix: {},
        searchRequest: {},
        enforceSearchRequest: { type: Boolean },
        searchQueryPlaceholder: {},
        statistics: {},
        maxItems: {},
        activeItemIdRouteParam: { type: [String, Function] },
        itemRoute: {},
        itemRouteIdParam: {},
        variant: {},
        bulkOperations: {},
        userSettingsSuffix: {},
        noScrollIntoView: { type: Boolean },
        wsSettings: {},
        mode: {},
        showNulls: { type: Boolean },
        showBadges: { type: Boolean }
    },
    emits: ["search-request-changed", "columns-changed", "bulk-selection-changed", "page-loaded", "load-error", "activate-item"],
    setup(__props, { emit: __emit }) {
        /**
         * Data table with virtual scrolling, search filters, sorting, column
         * selection, bulk selection and favourites.
         *
         * The state lives in composables and pure modules (see ./data-table/),
         * rows are components so that hovering or activating an item re-renders
         * only the affected rows.
         */
        const emit = __emit;
        const props = __props;
        const slots = useSlots();
        // -- user settings
        const settings = userSettings(userSettingsKey(props.grid, props.userSettingsSuffix, props.name));
        const storedSettings = settings.get({
            searchRequest: props.searchRequest,
            columns: [],
        });
        function saveUserSettings() {
            settings.set({ searchRequest: currentSearchRequest.value, columns: toColumnUserSettings(columns.value) });
        }
        // -- columns
        const columns = shallowRef(applyColumnUserSettings(createDefaultColumns(props.grid), storedSettings.columns));
        const selectedColumns = computed(() => columns.value.filter(col => col.selected));
        const selectedColumnNames = computed(() => namesOfSelectedColumns(columns.value));
        const sortColumn = computed(() => columns.value.find(col => col.sorted));
        const columnsModified = computed(() => areColumnsModified(columns.value, props.grid));
        const hasStatusBadge = computed(() => hasStatusBadgeColumn(props.grid));
        const columnViews = computed(() => selectedColumns.value.map(column => {
            const name = cssName(column.name);
            return {
                column,
                cssName: name,
                cellSlotName: 'cell-' + name,
                headerSlotName: 'column-' + name,
                cellClass: [
                    'data-table__cell',
                    'data-table__cell--size-' + column.size,
                    'data-table__cell--type-' + column.dataType,
                    'data-table__cell--column-' + name,
                ].join(' '),
                columnClass: [
                    'data-table__column',
                    'data-table__column--size-' + column.size,
                    'data-table__column--type-' + column.dataType,
                    'data-table__column--column-' + name,
                ].join(' '),
                translationPrefix: (props.translationPrefix || '') + column.name + '.',
                width: colWidth(column.size),
            };
        }));
        // -- capabilities & filters
        const capabilities = computed(() => {
            const effective = props.grid.capabilities
                ? { ...props.grid.capabilities }
                : { search: true, bulkOperations: false, favourites: false };
            if (effective.bulkOperations && (props.bulkOperations ?? []).length === 0) {
                effective.bulkOperations = false;
            }
            if (effective.favourites && !favouriteItemsService.isAvailable()) {
                effective.favourites = false;
            }
            effective.statistics = !!(effective.statistics
                && props.grid.statistics
                && props.grid.statistics.items.length > 0
                && props.statistics);
            return effective;
        });
        const filters = computed(() => createFilters(props.grid.filters, capabilities.value.favourites ? trans('data_table.favourites') : null));
        // -- search request
        const initialSearchRequest = resolveInitialSearchRequest({
            propSearchRequest: props.searchRequest,
            enforce: props.enforceSearchRequest,
            stored: storedSettings.searchRequest,
            gridDefault: props.grid.defaults && props.grid.defaults.searchRequest,
        });
        // Deliberately a deep ref: SearchFilters modifies the search request
        //   object in place and emits the same object, and the statistics watch
        //   it deeply. The same object is passed on, emitted and stored.
        const currentSearchRequest = ref(initialSearchRequest.searchRequest);
        if (initialSearchRequest.shouldEmit) {
            emit('search-request-changed', initialSearchRequest.searchRequest);
        }
        const maxItems = computed(() => props.maxItems || 10000);
        // -- layout, items, active item
        const layout = useDataTableLayout({
            mode: toRef(props, 'mode'),
            variant: toRef(props, 'variant'),
            maxItems,
            selectedColumns,
            onBodyResize: () => items.ensureRange(layout.visibleFrom.value, layout.visibleTo.value),
        });
        const { bodyEl, showTableColumns, scrollX, scrollY, rowsMarginLeft, listMarginLeft, dataWidth, headerWidth, } = layout;
        const items = useDataTableItems({
            source: toRef(props, 'source'),
            searchRequest: currentSearchRequest,
            prepareSearchRequest: sr => prepareSearchRequest(sr, favouriteItemsService.itemIds(props.grid.name)),
            sortColumn,
            selectedColumnNames,
            maxItems,
            rowHeight: layout.rowHeight,
            onPageLoaded: page => emit('page-loaded', page),
            onLoadError: error => emit('load-error', error),
        });
        const { slice, totalElements, moreElements, loading, tooManyResults, dataHeight, dataPaddingTop } = items;
        const active = useDataTableActiveItem({
            slice,
            noScrollIntoView: toRef(props, 'noScrollIntoView'),
            activeItemIdRouteParam: toRef(props, 'activeItemIdRouteParam'),
            onActivate: item => emit('activate-item', item),
        });
        const { activeItem, activateNextItem, activatePrevItem, setHovered } = active;
        active.activateItemByRoute();
        // -- bulk selection
        const selection = shallowRef(emptySelection());
        const selectedItemsCount = computed(() => countSelected(selection.value, totalElements.value));
        function toggleItemSelection(item) {
            selection.value = toggleSelection(selection.value, item.id);
            emit('bulk-selection-changed', createBulkOperationParams());
        }
        function toggleAllItemSelections() {
            selection.value = toggleAllSelections(selection.value);
            emit('bulk-selection-changed', createBulkOperationParams());
        }
        function createBulkOperationParams() {
            return {
                searchRequest: currentSearchRequest.value,
                selected: () => selectedIds(selection.value),
                excluded: () => excludedIds(selection.value),
            };
        }
        // -- favourites
        const favouritesVersion = ref(0);
        function toggleFavourite(item) {
            favouriteItemsService
                .toggleItem(props.grid.name, item.id)
                .then(() => {
                favouritesVersion.value++;
                if (hasFavouritesFilter(currentSearchRequest.value)) {
                    items.resetSlice();
                }
            });
        }
        function isFavourite(item) {
            return favouriteItemsService.isSelected(props.grid.name, item.id);
        }
        // -- events
        useDataTableEvents(props.name, {
            refresh: items.resetSlice,
            reload: items.reloadCurrent,
            navNext: activateNextItem,
            navPrev: activatePrevItem,
            activate: active.activateItemById,
            activateIfNoneActive: active.activateIfNoneActive,
        }, props.wsSettings);
        // -- context for the rows
        function createItemRoute(item) {
            const params = {};
            params[props.itemRouteIdParam || 'id'] = item.id;
            return {
                name: props.itemRoute, params: params,
            };
        }
        const context = {
            slots,
            columnViews,
            capabilities,
            hoveredItemId: active.hoveredItemId,
            activeItemId: active.activeItemId,
            selection,
            favouritesVersion,
            showNulls: toRef(props, 'showNulls'),
            itemRoute: toRef(props, 'itemRoute'),
            createItemRoute,
            setHovered,
            activate: active.activateItem,
            toggleSelection: toggleItemSelection,
            toggleFavourite,
            isFavourite,
            rowElements: active.rowElements,
        };
        provide(DATA_TABLE_CONTEXT, context);
        // -- root element classes and row actions offset
        const dataTableClass = computed(() => ({
            ['data-table--' + props.name]: true,
            'data-table--no-columns': !showTableColumns.value,
            ['data-table--variant-' + (props.variant ?? 'default')]: true,
            'data-table--with-status-badge': hasStatusBadge.value,
            'data-table--scroll-x': scrollX.value,
            'data-table--scroll-y': scrollY.value,
        }));
        const rowsEl = ref(null);
        // The row actions are positioned absolutely and follow the scroll
        //   position. One style property on the rows container instead of an
        //   inline style on each row, so that scrolling does not re-render rows.
        watchEffect(() => {
            rowsEl.value?.style.setProperty('--data-table-row-actions-margin-top', (-layout.scrollTop.value) + 'px');
        });
        function onBodyScroll() {
            layout.onBodyScroll();
            items.ensureRange(layout.visibleFrom.value, layout.visibleTo.value);
        }
        // -- lifecycle
        onMounted(() => {
            // limit to 2 x visible rows (min 20)
            items.pageSize.value = Math.max(20, 2 * layout.visibleItemsCount.value);
            // loads the items!
            items.loadItems(0);
            emitColumnsChanged();
        });
        // -- search
        function onSearchRequestChanged(newSearchRequest) {
            currentSearchRequest.value = newSearchRequest;
            items.resetSlice();
            layout.scrollToTop();
            saveUserSettings();
            selection.value = emptySelection();
            emit('search-request-changed', currentSearchRequest.value);
        }
        // -- column actions
        function emitColumnsChanged() {
            emit('columns-changed', columns.value);
        }
        function toggleColumn(column) {
            columns.value = toggleColumnSelected(columns.value, column.name);
            saveUserSettings();
            // the set of loaded attributes changed
            items.reloadCurrent();
            emitColumnsChanged();
        }
        function resizeColumn(column, size) {
            columns.value = resizeColumnTo(columns.value, column.name, size);
            saveUserSettings();
            emitColumnsChanged();
        }
        function sortColumns(newColumnsOrder) {
            columns.value = reorderColumns(columns.value, newColumnsOrder);
            saveUserSettings();
            emitColumnsChanged();
        }
        function resetColumns() {
            columns.value = resetColumnsToDefaults(columns.value, props.grid);
            saveUserSettings();
            items.reloadCurrent();
            emitColumnsChanged();
        }
        function onColumnSortToggle(column) {
            columns.value = toggleColumnSort(columns.value, column.name);
            items.resetSlice();
            saveUserSettings();
            emitColumnsChanged();
        }
        // -- statistics
        function loadStatistics() {
            if (!props.statistics) {
                return Promise.reject();
            }
            return props.statistics.get(currentSearchRequest.value, [] /* TODO */);
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                class: _normalizeClass(["data-table", dataTableClass.value])
            }, [
                _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                        _renderSlot(_ctx.$slots, "title", {}, () => [
                            (__props.title)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                                    _createElementVNode("h1", _hoisted_4, _toDisplayString(__props.title), 1),
                                    _cache[6] || (_cache[6] = _createTextVNode()),
                                    (__props.description)
                                        ? (_openBlock(), _createElementBlock("div", {
                                            key: 0,
                                            class: "data-table__description",
                                            title: __props.description
                                        }, _toDisplayString(__props.description), 9, _hoisted_5))
                                        : _createCommentVNode("", true),
                                    _cache[7] || (_cache[7] = _createTextVNode()),
                                    (__props.helpCode)
                                        ? (_openBlock(), _createBlock(Help, {
                                            key: 1,
                                            code: __props.helpCode
                                        }, null, 8, ["code"]))
                                        : _createCommentVNode("", true),
                                    _cache[8] || (_cache[8] = _createTextVNode()),
                                    (__props.showBadges)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.grid.badges, (badge, index) => {
                                                return (_openBlock(), _createBlock(BadgeComponent, {
                                                    class: "data-table__badge",
                                                    key: index
                                                }, {
                                                    default: _withCtx(() => [
                                                        _createTextVNode(_toDisplayString(badge), 1)
                                                    ]),
                                                    _: 2
                                                }, 1024));
                                            }), 128))
                                        ]))
                                        : _createCommentVNode("", true)
                                ]))
                                : _createCommentVNode("", true)
                        ]),
                        _cache[9] || (_cache[9] = _createTextVNode()),
                        (capabilities.value.search && currentSearchRequest.value)
                            ? (_openBlock(), _createBlock(SearchFilters, {
                                key: 0,
                                name: __props.name,
                                filters: filters.value,
                                "no-search-query": __props.grid.capabilities && __props.grid.capabilities.searchQueries === false,
                                "search-request": currentSearchRequest.value,
                                "search-query-placeholder": __props.searchQueryPlaceholder || _ctx.$t('search.placeholder'),
                                onKeydown: [
                                    _withKeys(_withModifiers(_unref(activateNextItem), ["exact", "stop"]), ["down"]),
                                    _withKeys(_withModifiers(_unref(activatePrevItem), ["exact", "stop"]), ["up"])
                                ],
                                onUpdate: onSearchRequestChanged
                            }, null, 8, ["name", "filters", "no-search-query", "search-request", "search-query-placeholder", "onKeydown"]))
                            : _createCommentVNode("", true)
                    ]),
                    _cache[10] || (_cache[10] = _createTextVNode()),
                    _createElementVNode("div", _hoisted_7, [
                        (currentSearchRequest.value)
                            ? _renderSlot(_ctx.$slots, "actions", {
                                searchRequest: currentSearchRequest.value,
                                item: _unref(activeItem)
                            }, undefined, undefined, 0)
                            : _createCommentVNode("", true)
                    ])
                ]),
                _cache[20] || (_cache[20] = _createTextVNode()),
                _createElementVNode("div", _hoisted_8, [
                    _createElementVNode("div", _hoisted_9, [
                        _createElementVNode("div", {
                            class: "data-table__columns",
                            style: _normalizeStyle({ width: (_unref(headerWidth) ? _unref(headerWidth) + 'px' : 'auto'), marginLeft: _unref(rowsMarginLeft) + 'px' })
                        }, [
                            _createElementVNode("div", _hoisted_10, [
                                (capabilities.value.bulkOperations)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                        _createElementVNode("input", {
                                            checked: selection.value.allSelected,
                                            onClick: _cache[0] || (_cache[0] = ($event) => (toggleAllItemSelections())),
                                            type: "checkbox",
                                            name: "select-all"
                                        }, null, 8, _hoisted_12)
                                    ]))
                                    : _createCommentVNode("", true),
                                _cache[12] || (_cache[12] = _createTextVNode()),
                                _createElementVNode("div", _hoisted_13, [
                                    (_unref(loading) && _unref(totalElements) === 0)
                                        ? (_openBlock(), _createBlock(Spinner, {
                                            key: 0,
                                            loading: true
                                        }))
                                        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                            (selectedItemsCount.value > 0 && selectedItemsCount.value < _unref(totalElements))
                                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                    _createTextVNode(_toDisplayString(selectedItemsCount.value) + " /\n                                ", 1)
                                                ], 64))
                                                : _createCommentVNode("", true),
                                            _createTextVNode(" " + _toDisplayString(_unref(numberFilter)(_unref(totalElements))) + " ", 1),
                                            (selectedItemsCount.value > 0 && __props.bulkOperations && __props.bulkOperations.length > 0)
                                                ? (_openBlock(), _createBlock(DataTableBulkOperations, {
                                                    key: 1,
                                                    operations: __props.bulkOperations,
                                                    params: createBulkOperationParams,
                                                    "button-variant": __props.variant === 'condensed' ? 'small' : undefined
                                                }, null, 8, ["operations", "button-variant"]))
                                                : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                                    _createTextVNode(_toDisplayString(_ctx.$te(__props.name + '._items') ? _ctx.$t(__props.name + '._items') : (_ctx.$te(__props.name + '.items') ? _ctx.$t(__props.name + '.items') : _ctx.$t('items'))), 1)
                                                ], 64)),
                                            _cache[11] || (_cache[11] = _createTextVNode()),
                                            _renderSlot(_ctx.$slots, "list-header-cell")
                                        ], 64))
                                ])
                            ]),
                            _cache[14] || (_cache[14] = _createTextVNode()),
                            (_unref(showTableColumns))
                                ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(columnViews.value, (view) => {
                                    return (_openBlock(), _createElementBlock("div", {
                                        key: view.column.name,
                                        class: _normalizeClass(view.columnClass),
                                        title: view.column.label
                                    }, [
                                        _renderSlot(_ctx.$slots, view.headerSlotName, {
                                            column: view.column
                                        }, () => [
                                            _createElementVNode("span", _hoisted_15, _toDisplayString(view.column.label), 1)
                                        ]),
                                        _cache[13] || (_cache[13] = _createTextVNode()),
                                        _createVNode(DataTableColumnSort, {
                                            column: view.column,
                                            onToggle: onColumnSortToggle
                                        }, null, 8, ["column"])
                                    ], 10, _hoisted_14));
                                }), 128))
                                : _createCommentVNode("", true),
                            _cache[15] || (_cache[15] = _createTextVNode()),
                            (capabilities.value.statistics)
                                ? (_openBlock(), _createBlock(DataTableStatistics, {
                                    key: 1,
                                    class: "data-table__statistics",
                                    grid: __props.grid,
                                    loader: loadStatistics,
                                    "reload-trigger": currentSearchRequest.value
                                }, null, 8, ["grid", "reload-trigger"]))
                                : _createCommentVNode("", true),
                            _cache[16] || (_cache[16] = _createTextVNode()),
                            (_unref(showTableColumns))
                                ? (_openBlock(), _createBlock(DataTableColumnsSelection, {
                                    key: 2,
                                    columns: columns.value,
                                    modified: columnsModified.value,
                                    onToggle: toggleColumn,
                                    onSort: sortColumns,
                                    onResize: resizeColumn,
                                    onReset: resetColumns
                                }, null, 8, ["columns", "modified"]))
                                : _createCommentVNode("", true)
                        ], 4)
                    ]),
                    _cache[19] || (_cache[19] = _createTextVNode()),
                    _createElementVNode("div", {
                        ref_key: "bodyEl",
                        ref: bodyEl,
                        class: "data-table__body",
                        onScrollPassive: onBodyScroll
                    }, [
                        _createElementVNode("div", {
                            class: "data-table__data",
                            style: _normalizeStyle({ width: (_unref(dataWidth) ? _unref(dataWidth) + 'px' : 'auto'), height: _unref(dataHeight) + 'px', paddingTop: _unref(dataPaddingTop) + 'px' }),
                            onMouseout: _cache[5] || (_cache[5] = ($event) => (_unref(setHovered)(null)))
                        }, [
                            _createElementVNode("div", {
                                class: "data-table__list",
                                onKeydown: [
                                    _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event) => (_unref(activateNextItem)()), ["exact", "prevent"]), ["tab"])),
                                    _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event) => (_unref(activateNextItem)()), ["exact", "prevent"]), ["j"])),
                                    _cache[3] || (_cache[3] = _withKeys(_withModifiers(($event) => (_unref(activatePrevItem)()), ["exact", "shift", "prevent"]), ["tab"])),
                                    _cache[4] || (_cache[4] = _withKeys(_withModifiers(($event) => (_unref(activatePrevItem)()), ["exact", "prevent"]), ["k"]))
                                ],
                                style: _normalizeStyle({ marginLeft: _unref(listMarginLeft) + 'px' })
                            }, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(slice).items, (item) => {
                                    return (_openBlock(), _createBlock(DataTableListRow, {
                                        key: item.id,
                                        item: item
                                    }, null, 8, ["item"]));
                                }), 128)),
                                _cache[17] || (_cache[17] = _createTextVNode()),
                                _withDirectives(_createElementVNode("div", _hoisted_16, [
                                    (_unref(tooManyResults))
                                        ? (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(_ctx.$t(_ctx.$te(__props.name + '.too_many_results') ? (__props.name + '.too_many_results') : 'search.too_many_results', { max_items: (_unref(numberFilter)(maxItems.value)) })), 1))
                                        : (_openBlock(), _createBlock(Spinner, {
                                            key: 1,
                                            loading: true
                                        }))
                                ], 512), [
                                    [_vShow, _unref(moreElements)]
                                ])
                            ], 36),
                            _cache[18] || (_cache[18] = _createTextVNode()),
                            (_unref(showTableColumns))
                                ? (_openBlock(), _createElementBlock("div", {
                                    key: 0,
                                    ref_key: "rowsEl",
                                    ref: rowsEl,
                                    class: "data-table__rows",
                                    style: _normalizeStyle({ marginLeft: _unref(rowsMarginLeft) + 'px' })
                                }, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(slice).items, (item) => {
                                        return (_openBlock(), _createBlock(DataTableRow, {
                                            key: item.id,
                                            item: item
                                        }, null, 8, ["item"]));
                                    }), 128))
                                ], 4))
                                : _createCommentVNode("", true)
                        ], 36)
                    ], 544)
                ])
            ], 2));
        };
    }
});
